import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  InsightsDonutCard,
  type InsightsDonutSegment,
} from "../../shared/layout/InsightsDonutCard";
import { useSummaryInsights } from "./useSummaryInsights";
import type {
  InsightsDatum,
  ProtocolInsightsParams,
} from "./protocolInsightsService";
import { formatTranslation } from "../../shared/utils/translationUtils";
import "./SummaryInsightsPanel.less";

const STATUS_COLORS: Record<string, string> = {
  published: "#14803C",
  activated: "#14803C",
  ready: "#0071CD",
  draft: "#C1462B",
};

const STATUS_LABEL_KEYS: Record<string, string> = {
  published: "protocolFilter.published",
  draft: "protocolFilter.draft",
  ready: "trialPreview.status.ready",
  activated: "protocols.summaryInsights.status.activated",
};

const PROTOCOL_DISPLAY_ORDER = ["published", "draft"];
const TRIAL_DISPLAY_ORDER = ["activated", "ready", "draft"];

const toSegments = (
  data: InsightsDatum[] | undefined,
  order: string[],
  t: (key: string) => string
): InsightsDonutSegment[] => {
  if (!data) return [];

  const byLabel = new Map(data.map((datum) => [datum.label, datum.value]));

  return order
    .filter((statusId) => byLabel.has(statusId))
    .map((statusId) => ({
      id: statusId,
      label: t(STATUS_LABEL_KEYS[statusId] ?? statusId),
      value: byLabel.get(statusId) ?? 0,
      color: STATUS_COLORS[statusId] ?? "#9CA3AF",
    }));
};

const sum = (data?: InsightsDatum[]): number =>
  (data ?? []).reduce((total, datum) => total + datum.value, 0);

export const SummaryInsightsPanel: React.FC<ProtocolInsightsParams> = (
  params
) => {
  const { t } = useTranslation();
  // i18n.ts sets `returnObjects: true`, so t() resolves to `{ message }`,
  // not a string — every translated value rendered as text (or handed to a
  // child as a string prop) must go through formatTranslation first.
  const translate = (key: string): string => formatTranslation(t(key));
  const { protocolInsights, trialInsights, isLoading, isError } =
    useSummaryInsights(params);

  const hasToastedRef = useRef(false);

  useEffect(() => {
    if (isError && !hasToastedRef.current) {
      toast.error(translate("protocols.summaryInsights.toast.loadError"));
      hasToastedRef.current = true;
    }

    if (!isError) {
      hasToastedRef.current = false;
    }
  }, [isError, t]);

  if (isError) {
    return null;
  }

  return (
    <div className="summary-insights-panel">
      <span className="summary-insights-panel__title">
        {translate("protocols.summaryInsights.title")}
      </span>

      <div className="summary-insights-panel__cards">
        <InsightsDonutCard
          id="protocol"
          title={translate("protocols.summaryInsights.protocol")}
          loading={isLoading}
          total={sum(protocolInsights)}
          segments={toSegments(
            protocolInsights,
            PROTOCOL_DISPLAY_ORDER,
            translate
          )}
        />
        <InsightsDonutCard
          id="trial"
          title={translate("protocols.summaryInsights.trial")}
          loading={isLoading}
          total={sum(trialInsights)}
          segments={toSegments(trialInsights, TRIAL_DISPLAY_ORDER, translate)}
        />
      </div>
    </div>
  );
};

export default SummaryInsightsPanel;
